import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  Vault, Kanban, FolderLock, UserCircle, SignOut, List, Sparkle, Scales,
  BookBookmark, Heart, Lightning, CreditCard, Gear, Question, House, CaretRight,
  Crown, ArrowUpRight, BookOpen, ChatsCircle,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import VaultLogo from "@/components/VaultLogo";

const BILLING_ENABLED = process.env.REACT_APP_BILLING_ENABLED === "true";

// Sidebar IA (grouped). Children render under a collapsible parent.
const GROUPS = [
  {
    section: null,
    items: [
      { to: "/profile",   label: "Profile",   icon: UserCircle, stripe: "var(--bolt-sky)" },
      { to: "/dashboard", label: "Dashboard", icon: House,      stripe: "var(--bolt-mustard)" },
    ],
  },
  {
    section: "BUILD",
    items: [
      {
        to: "/vault", label: "Vault", icon: Vault, stripe: "var(--brand-primary)",
        children: [
          { to: "/documents", label: "Document Safe", icon: FolderLock,   stripe: "var(--bolt-cobalt)" },
          { to: "/examples",  label: "Personal Bank", icon: BookBookmark, stripe: "var(--bolt-sage)" },
        ],
      },
    ],
  },
  {
    section: "APPLY",
    items: [
      { to: "/autofill", label: "Autofill", icon: Lightning, stripe: "var(--bolt-sage)" },
      // Saved Letters merged into Vault Coach → Cover Letter → Saved (Phase 2 refactor).
    ],
  },
  {
    section: "INSIGHTS",
    items: [
      { to: "/vault-coach", label: "Vault Coach", icon: Sparkle, stripe: "var(--bolt-mustard)" },
      { to: "/tracker",     label: "Tracker",     icon: Kanban,  stripe: "var(--bolt-plum)"   },
      { to: "/compare",     label: "Compare",     icon: Scales,  stripe: "var(--bolt-cobalt)" },
    ],
  },
  {
    section: "SUPPORT",
    items: [
      { to: "/wellbeing", label: "Wellbeing", icon: Heart,        stripe: "var(--bolt-blush)" },
      { to: "/community", label: "Community", icon: ChatsCircle,  stripe: "var(--bolt-sage)" },
      { to: "/guide",     label: "Guide",     icon: BookOpen,     stripe: "var(--bolt-cobalt)" },
      { to: "/contact",   label: "Help",      icon: Question,     stripe: "var(--bolt-mustard)" },
    ],
  },
  {
    section: "ACCOUNT",
    items: [
      { to: "/settings", label: "Settings", icon: Gear, stripe: "var(--bolt-mustard)" },
      ...(BILLING_ENABLED ? [{ to: "/billing", label: "Billing", icon: CreditCard, stripe: "var(--bolt-lime)" }] : []),
    ],
  },
];

// Routes whose Vault parent should auto-expand
const VAULT_CHILDREN = ["/documents", "/examples"];

function NavLinkItem({ item, active, indent = false }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      style={{ "--accent-stripe": item.stripe }}
      className={`nav-item flex items-center gap-3 px-3 py-2.5 rounded-md mb-1 ${active ? "is-active" : ""} ${indent ? "ml-4 pl-2" : ""}`}
    >
      <Icon size={indent ? 16 : 18} weight={active ? "fill" : "bold"} />
      <span className={`font-medium ${indent ? "text-[13px]" : "text-sm"}`}>{item.label}</span>
    </Link>
  );
}

export default function AppShell({ children, title }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [vaultExpanded, setVaultExpanded] = useState(
    () => loc.pathname === "/vault" || VAULT_CHILDREN.includes(loc.pathname)
  );

  // Keep Vault auto-expanded whenever the route lands on Vault or one of its children
  useEffect(() => {
    if (loc.pathname === "/vault" || VAULT_CHILDREN.includes(loc.pathname)) {
      setVaultExpanded(true);
    }
  }, [loc.pathname]);

  const doLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="min-h-screen noise">
      {/* Top bar */}
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 brand-link" data-testid="brand-link">
            <VaultLogo size={28} />
            <span className="font-display text-2xl font-semibold tracking-tight">JobVault</span>
          </Link>
          <div className="flex items-center gap-3">
            {BILLING_ENABLED && user?.tier === "free" && loc.pathname !== "/billing" && (
              <Link to="/billing" data-testid="topbar-upgrade-cta"
                className="hidden sm:inline-flex items-center gap-1.5 glass-pill text-[var(--brand-ink)] px-3 py-2 text-sm rounded-md font-medium hover:-translate-y-0.5 transition-transform">
                <Sparkle size={14} weight="fill" className="text-[var(--brand-primary)]"/> Upgrade
              </Link>
            )}
            {BILLING_ENABLED && (user?.tier === "premium" || user?.tier === "unlimited") && (
              <Link to="/billing" data-testid="topbar-tier-badge"
                title="Manage subscription"
                className="hidden sm:inline-flex items-center gap-1.5 glass-pill px-2.5 py-1.5 text-xs rounded-full font-medium">
                <Crown size={12} weight="fill" className="text-[var(--bolt-mustard)]"/> {user.tier === "unlimited" ? "Unlimited" : "Premium"}
              </Link>
            )}
            <span className="hidden md:inline font-mono-d text-xs text-[var(--brand-muted)]" data-testid="user-email">{user?.email}</span>
            <button onClick={doLogout} data-testid="logout-button"
              className="btn-hard bg-white px-3 py-2 text-sm flex items-center gap-1.5 rounded-md">
              <SignOut size={16} weight="bold" /> Logout
            </button>
            <button onClick={() => setOpen(!open)} className="lg:hidden btn-hard bg-white p-2 rounded-md" data-testid="mobile-menu-toggle">
              <List size={18} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        <aside className={`${open ? "block" : "hidden"} lg:block`}>
          <nav className="card-hard rounded-md p-3 sticky top-6" data-testid="sidebar-nav">
            {GROUPS.map((group, gi) => (
              <div key={gi} className={gi > 0 ? "mt-3" : ""}>
                {group.section && (
                  <p className="label-tiny text-[var(--brand-muted)] px-3 mt-2 mb-1.5" data-testid={`section-${group.section.toLowerCase()}`}>
                    /{group.section.toLowerCase()}
                  </p>
                )}
                {group.items.map((item) => {
                  const active = loc.pathname === item.to;
                  if (item.children) {
                    const isVaultGroup = item.to === "/vault";
                    return (
                      <div key={item.to}>
                        <div className="flex items-center group">
                          <NavLinkItem item={item} active={active} />
                          {isVaultGroup && (
                            <button
                              onClick={() => setVaultExpanded((v) => !v)}
                              data-testid="vault-expand-toggle"
                              aria-label={vaultExpanded ? "collapse" : "expand"}
                              className="-ml-1 p-1.5 rounded hover:bg-[var(--pastel-yellow)] transition shrink-0"
                            >
                              <CaretRight
                                size={14}
                                weight="bold"
                                className={`transition-transform ${vaultExpanded ? "rotate-90" : ""}`}
                              />
                            </button>
                          )}
                        </div>
                        {vaultExpanded && item.children.map((child) => (
                          <NavLinkItem key={child.to} item={child} active={loc.pathname === child.to} indent />
                        ))}
                      </div>
                    );
                  }
                  return <NavLinkItem key={item.to} item={item} active={active} />;
                })}
              </div>
            ))}
          </nav>
        </aside>

        <main>
          {title && (
            <div className="mb-8">
              <p className="label-tiny text-[var(--brand-muted)] mb-2">/{title.toLowerCase()}</p>
              <h1 className="font-display text-4xl lg:text-5xl font-semibold tracking-tight">{title}</h1>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
